import { useState } from 'react';
import {
  ListItem,
  majorScale,
  Pane,
  Text,
  UnorderedList,
  Tooltip,
  InfoSignIcon,
  Dialog,
  SmallCrossIcon,
  Heading,
  Paragraph,
} from 'evergreen-ui';
import styled from '@emotion/styled';
import { DRESS_CODE } from '../../../helpers/enums';
import { formatEventTimeZone } from '../../../helpers/functions';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import get from 'lodash/get';
import Btn from '../../../components/btn/btn';

const MainInstructionsContainer = styled(Pane)`
  .instruction-list {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-left: ${majorScale(1)}px;
    gap: ${majorScale(2)}px;
  }
`;

const DialogInstructionsContainer = styled(Pane)`
  .instruction-list {
    display: block;
    margin-left: ${majorScale(1)}px;
  }
`;

const InstructionItem = ({ label, value, tooltip, showTooltip = false }) => (
  <ListItem
    color="inherit"
    fontSize="1em"
    margin={0}
    marginBottom={majorScale(1)}
    display="flex"
    alignItems="center"
  >
    <Text
      marginRight={majorScale(1)}
      fontWeight={700}
      color="inherit"
      fontSize="inherit"
    >
      {label}:
    </Text>
    {showTooltip && tooltip ? (
      <Tooltip content={tooltip} position="bottom-right">
        <Pane display="inherit">
          <InfoSignIcon />
        </Pane>
      </Tooltip>
    ) : (
      <Text color="inherit" fontSize="inherit">
        {value}
      </Text>
    )}
  </ListItem>
);

function EventDetailsInstructions({ event }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const {
    eventDressCodeTerms,
    hatAcceptedDescription,
    optionAgeDescription,
    optionDressCodeDescription,
    optionIdRequirementDescription,
    shoeTypeDescription,
    OptionAge: age,
    OptionLastEntry: lastEntry,
    OptionDressCode: dressCode,
    OptionIdRequirement: idRequirement,
    HatAccepted: hatAccepted,
    ShoeType: shoeType,
    customMessage,
    customMessageTitle,
  } = event;

  const formatAge = (ageValue) =>
    ageValue >= 1 && ageValue <= 100 ? `${ageValue}+` : 'Not Applicable';

  const formatBooleanValue = (value) => (value ? 'Yes' : 'No');

  const instructionItems = [
    {
      condition: age,
      label: 'Age',
      value: optionAgeDescription || formatAge(age),
      tooltip: optionAgeDescription,
    },
    {
      condition: lastEntry && Date.parse(lastEntry),
      label: 'Last entry',
      value: formatEventTimeZone(lastEntry),
    },
    {
      condition: shoeType,
      label: 'Shoe Type',
      value: shoeTypeDescription || shoeType,
      tooltip: shoeTypeDescription,
    },
    {
      condition: dressCode,
      label: 'Dress code',
      value: optionDressCodeDescription || DRESS_CODE[dressCode]?.label,
      tooltip: optionDressCodeDescription,
    },
    {
      condition: idRequirement,
      label: 'ID required',
      value:
        optionIdRequirementDescription ||
        formatBooleanValue(idRequirement === 'YES'),
      tooltip: optionIdRequirementDescription,
    },
    {
      condition: hatAccepted,
      label: 'Hat accepted',
      value: hatAcceptedDescription || formatBooleanValue(hatAccepted),
      tooltip: hatAcceptedDescription,
    },
  ];

  const renderInstructionsList = (showTooltips = true) => (
    <UnorderedList className="instruction-list" marginLeft={0}>
      {instructionItems
        .filter((item) => item.condition)
        .map((item, index) => (
          <InstructionItem
            key={index}
            label={item.label}
            value={item.value}
            tooltip={item.tooltip}
            showTooltip={showTooltips && !!item.tooltip}
          />
        ))}

      {!isEmpty(eventDressCodeTerms) &&
        map(eventDressCodeTerms, (item, index) => (
          <InstructionItem
            key={`dress-code-${index}`}
            label={get(item, 'fieldTitle')}
            value={get(item, 'fieldDesctiption')}
            tooltip={get(item, 'fieldDesctiption')}
            showTooltip={showTooltips}
          />
        ))}
    </UnorderedList>
  );

  const handleCloseDialog = () => setIsDialogOpen(false);

  return (
    <>
      {/* <MainInstructionsContainer>
        {renderInstructionsList()}
      </MainInstructionsContainer> */}

      <Btn
        look="tertiary-outlined"
        // display="flex"
        // alignItems="center"
        gap={majorScale(1)}
        fontSize="1em"
        fontWeight={700}
        onClick={() => setIsDialogOpen(true)}
        marginTop={majorScale(2)}
      >
        <InfoSignIcon /> Event instructions
      </Btn>

      <Dialog
        isShown={isDialogOpen}
        onCloseComplete={handleCloseDialog}
        hasFooter={false}
        hasHeader={false}
        width="800px"
        preventBodyScrolling={false}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEscapePress={false}
        contentContainerProps={{ padding: 0 }}
      >
        <Pane padding={majorScale(2)} paddingTop={6}>
          <Pane
            display="flex"
            justifyContent="flex-end"
            marginBottom={majorScale(1)}
          >
            <SmallCrossIcon
              size={24}
              cursor="pointer"
              onClick={handleCloseDialog}
            />
          </Pane>

          <Heading
            fontSize="1.5em"
            fontWeight={700}
            marginBottom={majorScale(2)}
          >
            Event Instructions
          </Heading>

          <Pane
            border="1px solid #C4C4C4"
            padding={majorScale(2)}
            borderRadius={majorScale(1)}
            marginBottom={majorScale(3)}
          >
            <DialogInstructionsContainer>
              {renderInstructionsList(false)}
            </DialogInstructionsContainer>
          </Pane>

          {customMessage && customMessageTitle && (
            <Pane
              background="#FDFDE7"
              padding={majorScale(2)}
              borderRadius={majorScale(1)}
            >
              <Heading
                fontSize="1.2em"
                fontWeight={700}
                marginBottom={majorScale(2)}
              >
                {customMessageTitle}
              </Heading>
              <Paragraph fontSize="1em" lineHeight="1.5">
                {customMessage}
              </Paragraph>
            </Pane>
          )}
        </Pane>
      </Dialog>
    </>
  );
}

export default EventDetailsInstructions;
